import React, { useState, useEffect } from "react";
import { useNavigate }    from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Box, Button, CircularProgress, IconButton,
    InputAdornment, Paper, TextField, Typography, Alert,
} from "@mui/material";
import EmailIcon         from "@mui/icons-material/Email";
import LockIcon          from "@mui/icons-material/Lock";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import api from "../api/axios";

const Login = ({ onLoginSuccess, onForgotPassword }) => {
    const navigate = useNavigate();
    const { t }    = useTranslation();

    const [email,       setEmail]       = useState("");
    const [password,    setPassword]    = useState("");
    const [mostrarPass, setMostrarPass] = useState(false);
    const [msg,         setMsg]         = useState("");
    const [loading,     setLoading]     = useState(false);

    useEffect(() => {
        const params      = new URLSearchParams(window.location.search);
        const tokenParam  = params.get("token");
        const nombreParam = params.get("nombre");
        const fotoParam   = params.get("foto");
        const emailParam  = params.get("email");
        const errorParam  = params.get("error");

        if (errorParam) {
            const msgs = {
                "timeout_google":      "Tiempo de espera agotado al conectar con Google.",
                "google_status_error": "Google devolvió un error. Inténtalo más tarde.",
                "no_id_token":         "No se recibió el token de Google.",
                "missing_code":        "Código de autenticación ausente.",
                "no_email":            "No se pudo obtener el email de Google.",
                "internal_error":      "Error interno al procesar el inicio de sesión.",
            };
            setMsg(msgs[errorParam] || `Error: ${errorParam}`);
            window.history.replaceState({}, document.title, "/login");
            return;
        }
        if (tokenParam) {
            localStorage.setItem("token",       tokenParam);
            localStorage.setItem("user_nombre", nombreParam || "");
            localStorage.setItem("user_foto",   fotoParam   || "");
            localStorage.setItem("user_email",  (emailParam || "").toLowerCase());
            window.history.replaceState({}, document.title, "/login");
            window.location.href = "/compare";
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true); setMsg("");
        try {
            const form = new FormData();
            form.append("username", email);
            form.append("password", password);
            const res = await api.post(`/auth/login`, form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 10000,
            });
            localStorage.setItem("token",       res.data.access_token);
            localStorage.setItem("user_nombre", res.data.nombre || "");
            localStorage.setItem("user_foto",   res.data.foto   || "");
            localStorage.setItem("user_email",  email.toLowerCase());
            if (onLoginSuccess) onLoginSuccess();
            else navigate("/compare");
        } catch (error) {
            setMsg(!error.response ? "Error de conexión. Verifica que estés conectado." : t("login.error"));
        } finally { setLoading(false); }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default", p: 2 }}>
            <Paper elevation={4} sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, textAlign: "center", color: "primary.main" }}>
                    {t("login.title")}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        type="email" label={t("login.email")} value={email}
                        onChange={(e) => setEmail(e.target.value)} required fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" fontSize="small" /></InputAdornment> }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                        type={mostrarPass ? "text" : "password"} label={t("login.password")}
                        value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setMostrarPass(v => !v)} edge="end" size="small">
                                        {mostrarPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    {msg && <Alert severity="error" sx={{ borderRadius: 2 }}>{msg}</Alert>}

                    <Button type="submit" variant="contained" fullWidth disabled={loading}
                            sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none", mt: 1 }}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : t("login.button")}
                    </Button>
                </Box>

                <Button fullWidth onClick={() => { if (onForgotPassword) onForgotPassword(); else navigate("/forgot"); }}
                        sx={{ mt: 2, textTransform: "none", color: "primary.main", fontWeight: 600 }}>
                    {t("login.forgot")}
                </Button>
            </Paper>
        </Box>
    );
};

export default Login;